
"use client";
import { UseMutationResult, useMutation } from "@tanstack/react-query";
import fetchData from "libs/fetch"; // Adjust the relative path based on file location
import { POST_UPSCALE_IMAGE } from "api"; // Adjust this constant for the endpoint

// Define the request interface for the upscale API
interface UpscaleImageRequest {
  input_filename: string; // Input image filename or URL
  model_id: string; // Directly provided model ID
  upscale_factor?: number; // Default: 2.0
  guidance_scale?: number; // Default: 4.0
  num_inference_steps?: number; // Default: 18
  prompt?: string; // Default: "upscaled image"
}

// Define the response interface
interface UpscaleImageResponse {
  image_url: string; // URL of the upscaled image
  record_id: string; // Record ID from the backend
  model_id: string; // Associated model ID
}

// Function to call the upscale API
const fetchUpscaleImage = async (
  requestData: UpscaleImageRequest
): Promise<UpscaleImageResponse> => {
  try {
    const formData = new FormData();
    formData.append("input_filename", requestData.input_filename);
    formData.append("model_id", requestData.model_id); // Pass model_id directly
    formData.append("upscale_factor", String(requestData.upscale_factor || 2.0));
    formData.append("guidance_scale", String(requestData.guidance_scale || 4.0));
    formData.append("num_inference_steps", String(requestData.num_inference_steps || 18));
    formData.append("prompt", requestData.prompt || "upscaled image");

    // Call fetchData to send the request
    const data = await fetchData(POST_UPSCALE_IMAGE, {
      method: "POST",
      body: formData,
    });
    return data as UpscaleImageResponse;
  } catch (error) {
    console.error("Error upscaling image:", error);
    throw error;
  }
};

// Custom hook for upscaling images
export const useFalForUpscale = (): UseMutationResult<
  UpscaleImageResponse,
  Error,
  UpscaleImageRequest
> => {
  return useMutation<UpscaleImageResponse, Error, UpscaleImageRequest>({
    mutationFn: async (requestData: UpscaleImageRequest) =>
      await fetchUpscaleImage(requestData),
  });
};

export default useFalForUpscale;

